import { colorPrimary } from '@atlas-frontend/design-tokens';
import { Divider } from 'antd';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import { Dayjs } from 'dayjs';
import * as React from 'react';
import { useMemo } from 'react';
import styled from 'styled-components';
import { getDayjs, ISODate } from '../utils';
import { Day, Holiday, MonthIndex } from './types';
import { findMonthDay, monthWeeks, getWeekdayAbbreviations } from './utils';

export interface DaySelectionProps {
  onToggle: (day: Dayjs, dateStr: ISODate) => void;
  selected: Set<string>;
}

export interface MonthCalendarProps extends DaySelectionProps {
  holidays: Holiday[];
  monthIndex: MonthIndex;
  showName?: boolean;
  size?: SizeType;
  year: number;
}

export function MonthCalendar({
  holidays,
  monthIndex,
  onToggle,
  selected,
  showName,
  size,
  year,
}: MonthCalendarProps) {
  const month = useMonthCalendar(year, monthIndex, holidays);
  const weekdays = useMemo(() => getWeekdayAbbreviations(), []);

  return (
    <MonthWrapper $size={size}>
      {showName && (
        <MonthHeader>
          {month.name} {year}
        </MonthHeader>
      )}
      <Divider />
      <MonthBody>
        {weekdays.map((weekday) => (
          <WeekDay key={`${month.month + 1}-${weekday}`}>{weekday}</WeekDay>
        ))}
        {month.weeks.flatMap((week) =>
          week.map((day) => (
            <WeekDay
              key={day.dateStr}
              className={className(day, selected)}
              onClick={() => onToggle(day.date, day.dateStr)}
            >
              {day.isCurrentMonth ? day.date.date() : ''}
            </WeekDay>
          ))
        )}
      </MonthBody>
    </MonthWrapper>
  );
}

function useMonthCalendar(
  year: number,
  index: MonthIndex,
  holidays: Holiday[]
) {
  return useMemo(() => {
    const month = monthWeeks(year, index as MonthIndex);
    holidays
      .filter(
        (holiday) =>
          index === holiday.month && (!holiday.year || holiday.year === year)
      )
      .forEach((holiday) => {
        const date = getDayjs(
          new Date(year, holiday.month, holiday.dayOfMonth)
        )!;
        const day = findMonthDay(date, month);
        if (day) {
          day.isHoliday = true;
        }
      });
    return month;
  }, [holidays, index, year]);
}

function className(day: Day, selected: Set<string>) {
  if (!day.isCurrentMonth) {
    return undefined;
  }

  if (selected.has(day.dateStr)) {
    return 'selected';
  } else if (day.isHoliday) {
    return 'holiday';
  }
  return undefined;
}
const MonthWrapper = styled.div<{ $size?: string }>`
  --cell-width: ${(props) => (props.$size === 'small' ? '24px' : '64px')};
  width: calc(7 * var(--cell-width) + 6 * 12px);
  .ant-divider {
    margin-top: 12px;
  }
`;

const MonthHeader = styled.div`
  justify-content: center;
  text-align: center;
  text-transform: capitalize;
  width: 100%;
`;

const MonthBody = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(7, var(--cell-width));
  .holiday {
    background-color: #f5f5f5;
  }
  .selected {
    background-color: ${colorPrimary};
    color: #ffffff;
  }
`;

const WeekDay = styled.div`
  border-radius: 4px;
  height: 24px;
  line-height: 24px;
  text-align: center;
  vertical-align: center;
  width: var(--cell-width);
`;
